import 'dayjs/locale/es'
import { CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { Space, Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import type { WeeklyMetricsResponse, WeeklyMetricSupplier } from '@api'

import { WeeklyLegend, WeeklyMetricsTableWrap, WeeklyMovingHeader } from './styled'

dayjs.locale('es')

const ACUM_CELL_BG = 'rgba(59, 89, 152, 0.2)'

const WeeklyMetricsTableTitle = styled(Typography.Title)`
  color: ${({ theme }) => theme.primaryColor} !important;
  font-size: 1.25rem !important;
  font-weight: 700 !important;
  padding: 0 !important;
  margin: 0 !important;
  text-align: center;
`
const CheckOutlinedStyled = styled(CheckOutlined)`
  svg {
    color: #00a63e !important;
  }
`
const CloseOutlinedStyled = styled(CloseOutlined)`
  svg {
    color: #e7000b !important;
  }
`

interface SupplierTableRow {
  key: string
  supplierLabel: string
  dayMetrics: { orders: number; breaches: number }[]
  weeklyTotal: { orders: number; breaches: number }
}

/** Summary block (totals) — rendered in Table.Summary, not in scroll body */
interface SummaryTableRow {
  key: string
  supplierLabel: string
  dayMetrics: { orders: number; breaches: number }[]
  weeklyTotal: { orders: number; breaches: number }
}

function formatMovingWeekDayLabel(date: string | Date) {
  const d = dayjs(date)
  const n = d.date()
  const raw = d.format('ddd').replace(/\./g, '').trim()
  const short = raw.length > 0 ? raw.charAt(0).toUpperCase() + raw.slice(1) : ''
  return `${n}-${short}`
}

function renderSummaryRows(summaryRows: SummaryTableRow[], dayCount: number) {
  return summaryRows.map(row => {
    let index = 0
    const cellClass = 'weekly-summary-total-cell'
    const useHighlight =
      row.key === 'total-suppliers' || row.key === 'transfers-subtotal' || row.key === 'grand-total'
    const cellApplyClass = useHighlight ? cellClass : ''
    const cells = [
      <Table.Summary.Cell
        key={`${row.key}-label`}
        index={index++}
        align="left"
        className={cellApplyClass}
      >
        {row.supplierLabel}
      </Table.Summary.Cell>,
    ]

    for (let i = 0; i < dayCount; i++) {
      const m = row.dayMetrics[i] ?? { orders: 0, breaches: 0 }
      cells.push(
        <Table.Summary.Cell
          key={`${row.key}-d${i}-o`}
          index={index++}
          align="center"
          className={cellApplyClass}
        >
          <span style={{ color: '#52c41a', fontWeight: 600 }}>
            {m.orders === 0 ? '-' : m.orders}
          </span>
        </Table.Summary.Cell>,
        <Table.Summary.Cell
          key={`${row.key}-d${i}-b`}
          index={index++}
          align="center"
          className={cellApplyClass}
        >
          <span style={{ color: '#ff4d4f', fontWeight: 600 }}>
            {m.breaches === 0 ? '-' : m.breaches}
          </span>
        </Table.Summary.Cell>,
      )
    }

    cells.push(
      <Table.Summary.Cell
        key={`${row.key}-w-o`}
        index={index++}
        align="center"
        className={cellClass}
      >
        <span className="metrics-cell-center" style={{ color: '#52c41a', fontWeight: 600 }}>
          {row.weeklyTotal.orders === 0 ? '-' : row.weeklyTotal.orders}
        </span>
      </Table.Summary.Cell>,
      <Table.Summary.Cell
        key={`${row.key}-w-b`}
        index={index++}
        align="center"
        className={cellClass}
      >
        <span className="metrics-cell-center" style={{ color: '#ff4d4f', fontWeight: 600 }}>
          {row.weeklyTotal.breaches === 0 ? '-' : row.weeklyTotal.breaches}
        </span>
      </Table.Summary.Cell>,
    )

    return <Table.Summary.Row key={row.key}>{cells}</Table.Summary.Row>
  })
}

export interface WeeklyMetricsTableProps {
  data: WeeklyMetricsResponse | undefined
  isLoading: boolean
}

const WeeklyMetricsTable = ({ data, isLoading }: WeeklyMetricsTableProps) => {
  const { t } = useTranslation()

  const { columns, dataRows, summaryRows, dayCount } = useMemo(() => {
    const weeklyAccumGroup: ColumnsType<SupplierTableRow>[number] = {
      title: t('admin_router.dashboard.weekly_table_weekly_total'),
      align: 'center' as const,
      children: [
        {
          title: (
            <span className="metrics-cell-center">
              <CheckOutlinedStyled style={{ fontSize: 12 }} />
            </span>
          ),
          align: 'center' as const,
          width: 56,
          onCell: () => ({ style: { background: ACUM_CELL_BG } }),
          render: (_value: unknown, record: SupplierTableRow) => (
            <span className="metrics-cell-center" style={{ color: '#52c41a', fontWeight: 500 }}>
              {record.weeklyTotal.orders === 0 ? '-' : record.weeklyTotal.orders}
            </span>
          ),
        },
        {
          title: (
            <span className="metrics-cell-center">
              <CloseOutlinedStyled style={{ fontSize: 11 }} />
            </span>
          ),
          align: 'center' as const,
          width: 56,
          onCell: () => ({ style: { background: ACUM_CELL_BG } }),
          render: (_value: unknown, record: SupplierTableRow) => (
            <span className="metrics-cell-center" style={{ color: '#ff4d4f', fontWeight: 500 }}>
              {record.weeklyTotal.breaches === 0 ? '-' : record.weeklyTotal.breaches}
            </span>
          ),
        },
      ],
    }
    if (!data) {
      return {
        columns: [
          {
            title: t('admin_router.dashboard.weekly_table_supplier'),
            dataIndex: 'supplierLabel',
            fixed: 'left',
            width: 200,
          },
          weeklyAccumGroup,
        ] as ColumnsType<SupplierTableRow>,
        dataRows: [] as SupplierTableRow[],
        summaryRows: [] as SummaryTableRow[],
        dayCount: 0,
      }
    }

    const suppliers = data.suppliers ?? []
    const transferList = data.transfers ?? []
    const showTransfersSection = data.transfers !== undefined
    const transferDaysFallback = transferList[0]?.days ?? []
    const daySources = suppliers[0]?.days?.length > 0 ? suppliers[0].days : transferDaysFallback
    const dayCountVal = daySources.length

    if (dayCountVal === 0) {
      return {
        columns: [] as ColumnsType<SupplierTableRow>,
        dataRows: [] as SupplierTableRow[],
        summaryRows: [] as SummaryTableRow[],
        dayCount: 0,
      }
    }

    const supplierRows: SupplierTableRow[] = suppliers.map((s: WeeklyMetricSupplier) => ({
      key: `supplier-${s.supplierId}`,
      supplierLabel: s.tradeName,
      dayMetrics: s.days.map(d => ({ orders: d.orders, breaches: d.breaches })),
      weeklyTotal: s.days.reduce(
        (acc, d) => ({ orders: acc.orders + d.orders, breaches: acc.breaches + d.breaches }),
        { orders: 0, breaches: 0 },
      ),
    }))

    const totalSuppliersDayMetrics = Array.from({ length: dayCountVal }, (_, i) =>
      suppliers.reduce(
        (acc, s) => ({
          orders: acc.orders + (s.days[i]?.orders ?? 0),
          breaches: acc.breaches + (s.days[i]?.breaches ?? 0),
        }),
        { orders: 0, breaches: 0 },
      ),
    )

    const totalSuppliersWeekly = totalSuppliersDayMetrics.reduce(
      (acc, d) => ({ orders: acc.orders + d.orders, breaches: acc.breaches + d.breaches }),
      { orders: 0, breaches: 0 },
    )

    const transferDayMetrics = showTransfersSection
      ? Array.from({ length: dayCountVal }, (_, i) =>
          transferList.reduce(
            (acc, s) => ({
              orders: acc.orders + (s.days[i]?.orders ?? 0),
              breaches: acc.breaches + (s.days[i]?.breaches ?? 0),
            }),
            { orders: 0, breaches: 0 },
          ),
        )
      : []
    const transferWeekly = transferDayMetrics.reduce(
      (acc, d) => ({ orders: acc.orders + d.orders, breaches: acc.breaches + d.breaches }),
      { orders: 0, breaches: 0 },
    )

    const grandDayMetrics = showTransfersSection
      ? totalSuppliersDayMetrics.map((tot, i) => ({
          orders: tot.orders + (transferDayMetrics[i]?.orders ?? 0),
          breaches: tot.breaches + (transferDayMetrics[i]?.breaches ?? 0),
        }))
      : totalSuppliersDayMetrics.map(tot => ({ orders: tot.orders, breaches: tot.breaches }))

    const grandWeekly = grandDayMetrics.reduce(
      (acc, d) => ({ orders: acc.orders + d.orders, breaches: acc.breaches + d.breaches }),
      { orders: 0, breaches: 0 },
    )

    const summaryBlock: SummaryTableRow[] = []

    if (showTransfersSection) {
      summaryBlock.push({
        key: 'total-suppliers',
        supplierLabel: t('admin_router.dashboard.weekly_table_total_suppliers'),
        dayMetrics: totalSuppliersDayMetrics,
        weeklyTotal: totalSuppliersWeekly,
      })
      for (const ts of transferList) {
        const dayMetrics = ts.days.map(d => ({ orders: d.orders, breaches: d.breaches }))
        const weeklyTotal = ts.days.reduce(
          (acc, d) => ({ orders: acc.orders + d.orders, breaches: acc.breaches + d.breaches }),
          { orders: 0, breaches: 0 },
        )
        summaryBlock.push({
          key: `transfer-supplier-${ts.supplierId}`,
          supplierLabel: ts.tradeName,
          dayMetrics,
          weeklyTotal,
        })
      }
      if (transferList.length > 0) {
        summaryBlock.push({
          key: 'transfers-subtotal',
          supplierLabel: t('admin_router.dashboard.weekly_table_transfers_total'),
          dayMetrics: transferDayMetrics,
          weeklyTotal: transferWeekly,
        })
      }
    }

    summaryBlock.push({
      key: 'grand-total',
      supplierLabel: t('admin_router.dashboard.weekly_table_total'),
      dayMetrics: grandDayMetrics,
      weeklyTotal: grandWeekly,
    })

    const dayGroups: ColumnsType<SupplierTableRow> = Array.from(
      { length: dayCountVal },
      (_, i) => ({
        title: formatMovingWeekDayLabel(daySources[i].date),
        align: 'center' as const,
        children: [
          {
            title: <CheckOutlinedStyled style={{ fontSize: 12 }} />,
            align: 'center' as const,
            width: 56,
            render: (_value: unknown, record: SupplierTableRow) => {
              const { orders } = record.dayMetrics[i] ?? { orders: 0, breaches: 0 }
              return (
                <span style={{ color: '#52c41a', fontWeight: 500 }}>
                  {orders === 0 ? '-' : orders}
                </span>
              )
            },
          },
          {
            title: <CloseOutlinedStyled style={{ fontSize: 11 }} />,
            align: 'center' as const,
            width: 56,
            render: (_value: unknown, record: SupplierTableRow) => {
              const { breaches } = record.dayMetrics[i] ?? { orders: 0, breaches: 0 }
              return (
                <span style={{ color: '#ff4d4f', fontWeight: 500 }}>
                  {breaches === 0 ? '-' : breaches}
                </span>
              )
            },
          },
        ],
      }),
    )

    const cols: ColumnsType<SupplierTableRow> = [
      {
        title: t('admin_router.dashboard.weekly_table_supplier'),
        dataIndex: 'supplierLabel',
        fixed: 'left',
        width: 200,
      },
      ...dayGroups,
      weeklyAccumGroup,
    ]

    return {
      columns: cols,
      dataRows: supplierRows,
      summaryRows: summaryBlock,
      dayCount: dayCountVal,
    }
  }, [data, t])

  return (
    <WeeklyMetricsTableWrap>
      <WeeklyMovingHeader>
        <WeeklyMetricsTableTitle level={5}>
          {t('admin_router.dashboard.weekly_moving_title')}
        </WeeklyMetricsTableTitle>
      </WeeklyMovingHeader>
      <WeeklyLegend>
        <Space size={4}>
          <CheckOutlinedStyled />
          <span style={{ color: '#00A63E' }}>
            {t('admin_router.dashboard.weekly_legend_orders')}
          </span>
        </Space>
        <Space size={4}>
          <CloseOutlinedStyled />
          <span style={{ color: '#E7000B' }}>
            {t('admin_router.dashboard.weekly_legend_breaches')}
          </span>
        </Space>
      </WeeklyLegend>
      <Table<SupplierTableRow>
        loading={isLoading}
        size="small"
        bordered
        pagination={false}
        scroll={{ x: 'max-content', y: 43 * 10 }}
        columns={columns}
        dataSource={dataRows}
        summary={() => (
          <Table.Summary fixed="bottom">{renderSummaryRows(summaryRows, dayCount)}</Table.Summary>
        )}
      />
    </WeeklyMetricsTableWrap>
  )
}

export { WeeklyMetricsTable }
